// Proposal bookkeeping: the parts of a proposed mutation that are audit
// residue rather than domain changes. One registry drives every consumer, so
// adding a field/tag here automatically updates all three behaviors:
//
//   1. isProposalBookkeepingPath — exempts the path from the
//      changed-since-proposed drift check (bookkeeping moves on every touch
//      and would flag everything).
//   2. withoutProposalBookkeeping — strips it before the already-applied
//      comparison and the rendered diff (it is not a change the reviewer is
//      deciding on).
//   3. restampProposalBookkeepingTimestamps — rewrites timestamp ops to the
//      actual apply time (model setters stamp them with the proposal BUILD
//      time, which is stale by construction — a queued review can sit for
//      days, and an interim touch even makes the stored value move time
//      backwards).

import type { BundleEntry, Resource } from '@medplum/fhirtypes'
import {
  BATCH_OPERATION_SYSTEM,
  QUEUED_EDITS_ORIGIN_SYSTEM,
} from '#src/codeSystems.ts'
import { base64Decode, base64Encode } from '#src/encoding.ts'
import { isFhirPathPatchParameters } from '#src/fhirpath-patch/index.ts'

// Tag systems that are proposal/batch bookkeeping: the queued-edits origin
// backlink, and the batch-operation stamp used by backend batch flows and by
// legacy (pre-origin-tag) proposal bundles.
const PROPOSAL_BOOKKEEPING_TAG_SYSTEMS: readonly string[] = [
  QUEUED_EDITS_ORIGIN_SYSTEM,
  BATCH_OPERATION_SYSTEM,
]

interface BookkeepingTimestampFields {
  [resourceType: string]: readonly string[]
}

// Per-resource-type audit-timestamp fields whose value is a revision stamp —
// "when this record was last logically changed" — written as a side effect of
// the mutation (model setters, or the R4B element definition itself), never
// meaningful data in a proposal. Registration is per type because generic
// field names differ in meaning across types (`Composition.date` is a
// revision stamp; `RiskAssessment.date` is the assessment time).
//
// Deliberately EXCLUDED: authorship/attestation/creation times — authoredOn,
// issued, recordedDate, dateAsserted, created, DocumentReference.date. Those
// say when something clinically happened; overwriting them at apply would
// corrupt the record.
const PROPOSAL_BOOKKEEPING_TIMESTAMP_FIELDS: BookkeepingTimestampFields = {
  // "The date and time of last modification to this task" — ExtendedTask
  // setters bump it on every mutation.
  Task: ['lastModified'],
  // "The composition editing time, when the composition was last logically
  // changed."
  Composition: ['date'],
  // Definitional resources: `date` is "the date … when the resource was
  // last significantly changed" per the R4B canonical-resource pattern.
  Questionnaire: ['date'],
  PlanDefinition: ['date'],
  ActivityDefinition: ['date'],
  Library: ['date'],
  Measure: ['date'],
  ValueSet: ['date'],
  CodeSystem: ['date'],
  ConceptMap: ['date'],
}

// Whether a touched FHIRPath is proposal bookkeeping rather than a domain
// change: anything under `meta` (tags ride there), or a registered
// audit-timestamp field of the path's own resource type (paths are
// type-rooted fhirpath expressions, e.g. `Task.lastModified` — matching must
// be type-scoped, or `Composition.date` in the registry would also exempt
// clinically meaningful `date` fields on other types).
export function isProposalBookkeepingPath(path: string): boolean {
  if (/(^|\.)meta(\.|$)/.test(path)) {
    return true
  }
  return Object.entries(PROPOSAL_BOOKKEEPING_TIMESTAMP_FIELDS).some(
    ([resourceType, fields]) =>
      fields.some((field) => path === `${resourceType}.${field}`)
  )
}

// A deep clone of the resource with proposal bookkeeping removed: every
// bookkeeping tag (dropping an emptied `meta.tag` / `meta`) and the type's
// registered audit-timestamp fields. Comparing the stripped patched result
// against the stripped current state reveals whether a proposed entry's
// actual change has already been applied outside the review (see
// bundle-to-change-sets.ts). A resource that already absorbed one apply
// carries the stamp tags (possibly duplicated), so both sides must strip.
export function withoutProposalBookkeeping<TResource extends Resource>(
  resource: TResource
): TResource {
  const clone = structuredClone(resource)
  for (const field of PROPOSAL_BOOKKEEPING_TIMESTAMP_FIELDS[
    clone.resourceType
  ] ?? []) {
    Reflect.deleteProperty(clone, field)
  }
  if (!clone.meta?.tag) {
    return clone
  }
  const tags = clone.meta.tag.filter(
    (tag) => !PROPOSAL_BOOKKEEPING_TAG_SYSTEMS.includes(tag.system ?? '')
  )
  if (tags.length > 0) {
    clone.meta.tag = tags
  } else {
    delete clone.meta.tag
    if (Object.keys(clone.meta).length === 0) {
      delete clone.meta
    }
  }
  return clone
}

// Rewrite a PATCH entry's registered audit-timestamp ops to the given
// instant — the actual apply time, replacing the stale build-time stamp.
// Covers both wire forms (fhirpath-patch `Type.field` / add `Type` + name
// `field`; json-patch `/field`). No-op for entries whose target type
// registers no timestamp fields. Mutates the entry payload in place.
export function restampProposalBookkeepingTimestamps(
  entry: BundleEntry,
  at: string
): void {
  const targetType = entry.request?.url?.split('?')[0]?.split('/')[0]
  const fields = targetType
    ? (PROPOSAL_BOOKKEEPING_TIMESTAMP_FIELDS[targetType] ?? [])
    : []
  if (fields.length === 0) {
    return
  }

  const resource = entry.resource
  if (isFhirPathPatchParameters(resource)) {
    for (const param of resource.parameter ?? []) {
      if (param.name !== 'operation') {
        continue
      }
      const parts = param.part ?? []
      const type = parts.find((p) => p.name === 'type')?.valueCode
      if (type !== 'add' && type !== 'replace') {
        continue
      }
      const path = parts.find((p) => p.name === 'path')?.valueString
      const name = parts.find((p) => p.name === 'name')?.valueString
      const targeted = fields.some(
        (field) =>
          path === `${targetType}.${field}` ||
          (path === targetType && name === field)
      )
      if (!targeted) {
        continue
      }
      const valuePart = parts.find((p) => p.name === 'value')
      if (!valuePart) {
        continue
      }
      // Timestamps only — cover the primitive encodings a builder may use,
      // and refuse to clobber a value that is not actually a timestamp (a
      // registered field should only ever carry a build-time stamp; anything
      // else means the op is not the side-effect stamp this targets).
      if (isTimestamp(valuePart.valueDateTime)) {
        valuePart.valueDateTime = at
      } else if (isTimestamp(valuePart.valueInstant)) {
        valuePart.valueInstant = at
      } else if (isTimestamp(valuePart.valueString)) {
        valuePart.valueString = at
      }
    }
    return
  }

  if (
    resource?.resourceType === 'Binary' &&
    resource.contentType === 'application/json-patch+json' &&
    resource.data
  ) {
    let ops: { op: string; path: string; value?: unknown }[]
    try {
      ops = JSON.parse(base64Decode(resource.data))
    } catch {
      return
    }
    let changed = false
    for (const op of ops) {
      if (
        (op.op === 'add' || op.op === 'replace') &&
        fields.some((field) => op.path === `/${field}`) &&
        isTimestamp(op.value)
      ) {
        op.value = at
        changed = true
      }
    }
    if (changed) {
      resource.data = base64Encode(JSON.stringify(ops))
    }
  }
}

// A registered field's op value must itself look like an instant before it is
// replaced — the restamp corrects a stale stamp, it must never manufacture
// one over unexpected content.
function isTimestamp(value: unknown): value is string {
  return typeof value === 'string' && !Number.isNaN(Date.parse(value))
}
